import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { adminQuoteListQuery, adminShellQuery } from '@/queries';
import { useEffect, useState } from 'react';

import { NewQuote } from '@/components/admin/new-quote';
import { OpenRow } from '@/components/open-row';
import { Pager } from '@/components/pager';
import { usePagedList } from '@/lib/use-paged-list';
import { QuoteStatusBadge, formatDate } from '@/components/status-timeline';
import { Badge, EmptyState } from '@/components/ui';
import { formatCents, formatCentsOrNone } from '@/lib/money';
import { QUOTE_STATUS_LABEL, type Quote, type QuoteStatus } from '@/lib/types';

const FILTERS: { key: string; label: string; match: (s: QuoteStatus) => boolean }[] = [
  { key: 'open', label: 'Needs action', match: (s) => s === 'submitted' || s === 'proof_sent' },
  { key: 'active', label: 'In flight', match: (s) => ['priced', 'approved', 'deposit_paid', 'in_production', 'quality_check', 'shipped'].includes(s) },
  { key: 'closed', label: 'Closed', match: (s) => ['completed', 'declined', 'cancelled'].includes(s) },
  { key: 'all', label: 'All', match: () => true },
];

export default function AdminQuotesPage() {
  const search = useSearch({ strict: false }) as { filter?: string; for_prospect?: string };
  const filterKey = search.filter ?? 'open';
  const navigate = useNavigate();
  const filter = FILTERS.find((f) => f.key === filterKey) ?? FILTERS[0]!;
  /**
   * Filtered and paged BY THE DATABASE. R-264.
   *
   * This read 200 quotes and filtered them here. Past 200 the "All" tab
   * silently became "the newest 200", and every other tab was computed from
   * that truncated set, so "Needs action" could miss a quote that needed
   * action. A queue that quietly drops the oldest item is worse than no queue,
   * because people trust it.
   *
   * The counts on the tabs are counted over every row, not over this page: a
   * tab that says 3 has to mean 3 in the business.
   */
  // No search box on this screen, so the hook is here for the page number and
  // the reset to page one when the tab changes. That reset is the rule that
  // gets forgotten: filter to Closed while on page 8 and the screen asks for
  // page 8 of a list with two. R-272.
  const list = usePagedList<Quote>(
    ({ page }) => adminQuoteListQuery({ page, filter: filterKey }) as never,
    [filterKey],
  );
  const { setPage, rows: quotes, isFetching } = list;
  const data = list.data as { counts?: Record<string, number> } | undefined;

  /**
   * May this person open a quote?
   *
   * Asked of the permission matrix, not of a role name: `quotes.create` is a
   * column an admin can grant in the permissions screen, and staff do not have
   * it today. Already in the cache, because the admin shell loads it.
   */
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const mayCreate = (can.quotes ?? []).includes('create');

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Quote queue</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Price it, send a proof, convert it to an order.
          </p>
        </div>
        {/* The only way to start one. Every B2B job walks from here. R-275. */}
        {mayCreate && <NewQuote key={search.for_prospect ?? 'new'} forProspect={search.for_prospect} />}
      </div>

      <nav aria-label="Filter quotes" className="mb-6 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <Link
            key={f.key}
            to="/admin/quotes" search={{ filter: f.key }}
            aria-current={f.key === filterKey ? 'page' : undefined}
            className={
              'inline-flex h-10 items-center rounded-[10px] border px-3 text-sm font-semibold ' +
              (f.key === filterKey
                ? 'border-ink bg-ink text-panel'
                : 'border-line-strong bg-panel hover:border-ink/40')
            }
         >
            {f.label}
            {data?.counts?.[f.key] !== undefined && (
              <span className="ml-1.5 opacity-70">{data.counts[f.key]}</span>
            )}
          </Link>
        ))}
      </nav>

      {quotes.length === 0 ? (
        <EmptyState
          title="Nothing here"
          body={`No quotes match "${filter.label.toLowerCase()}" right now.`}
        />
      ) : (
        <div className="overflow-x-auto rounded-[10px] border border-line bg-panel">
          <table className="dt w-full min-w-[820px] text-left">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Customer</th>
                <th scope="col">Job</th>
                <th scope="col">Submitted</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <OpenRow
                  key={q.id}
                  label={`Open quote ${q.ref}`}
                  onOpen={() => void navigate({
                    to: '/admin/quotes/$ref', params: { ref: q.ref },
                  })}
                >
                <td>
                    <Link
                      to="/admin/quotes/$ref" params={{ ref: q.ref }}
                      className="font-mono text-sm font-bold text-brand-ink hover:underline"
                   >
                      {q.ref}
                    </Link>
                    {/* Attribution is visible at a glance because it decides money. */}
                    {q.app_originated && (
                      <span className="ml-2 align-middle">
                        <Badge tone="brand">App</Badge>
                      </span>
                    )}
                  </td>
                <td>
                    <span className="block font-semibold">
                      {q.contact.company ?? q.contact.name}
                    </span>
                    <span className="text-sm text-muted">{q.contact.email}</span>
                  </td>
                <td className="text-muted">
                    {q.qty} units &middot; {q.method_key}
                  </td>
                <td className="text-muted">{formatDate(q.created_at)}</td>
                <td>
                    <QuoteStatusBadge status={q.status} />
                  </td>
                <td className="num font-mono font-bold">
                    {formatCentsOrNone(q.total_cents ?? q.est_total_cents)}
                    {/* Only when there IS an estimate to qualify. `total_cents == null`
                        alone is also true for a quote nobody has priced, and that row
                        already reads "Not priced": it rendered "Not priced est". R-340. */}
                    {q.total_cents == null && q.est_total_cents != null && (
                      <span className="ml-1 text-[11px] font-normal text-muted">est</span>
                    )}
                  </td>
                </OpenRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager {...list.pager} noun="quote" onPage={setPage} />

      <p className="mt-4 text-[13px] text-muted">
        Statuses: {Object.values(QUOTE_STATUS_LABEL).join(', ')}.
      </p>
    </div>
  );
}
